import axios from "axios";
import { CheckToken } from "../../login/AccessToken";

/**
 * Nurse master APIs — backing the Nurse Management screen.
 *
 *   add_new_user/    POST create, with user_type "Nurse" — also creates the
 *                    linked login user, which is why creation goes through the
 *                    shared user endpoint instead of a nurse-specific one
 *   get_all_nurses/  GET  list (active + inactive, scoped to the caller)
 *   edit_nurse/      POST partial update — `nurse_id` identifies the row
 *   delete_nurse/    POST hard delete (nurse record + linked login user)
 *
 * All of them live on the main Django backend, so they use
 * REACT_APP_BACKEND_ROUTE_URL and follow the usual
 * { status, data | message } envelope. Every response is HTTP 200 — read
 * `status`, not the HTTP code. A body with is_valid:false means the header /
 * token check failed, which is exactly the case the global interceptor logs
 * the user out for, so these endpoints must NOT be excluded from it.
 */

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

export const NURSE_ROLES = [
  { value: "nurse", label: "Staff Nurse" },
  { value: "ward incharge", label: "Ward Incharge" },
  { value: "nursing superintendent", label: "Nursing Superintendent" },
];

// Bearer is not attached globally (see Axios-interceptor), so every call
// attaches it here after CheckToken() pre-refreshes an expired JWT.
function authHeaders() {
  CheckToken();
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  };
}

// POST add_new_user/ — the caller sets user_type "Nurse"; the endpoint creates
// the login user and the main_nurserecord row together.
export async function createNurse(payload) {
  const res = await axios.post(`${BASE}add_new_user/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

export async function getNurses() {
  const res = await axios.get(`${BASE}get_all_nurses/`, {
    headers: authHeaders(),
  });
  return Array.isArray(res?.data?.data) ? res.data.data : [];
}

// POST edit_nurse/ — partial update: only the keys present in the payload are
// written. `nurse_id` (the UUID column, not the numeric id) is mandatory.
export async function updateNurse(payload) {
  const res = await axios.post(`${BASE}edit_nurse/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

// POST delete_nurse/ — hard delete. For a reversible option the backend also
// exposes deactivate_facility_user/<id>/ with { type: "nurse_user" }; the list
// screen instead flips `active` through edit_nurse/.
export async function deleteNurse(nurseId) {
  const res = await axios.post(
    `${BASE}delete_nurse/`,
    { nurse_id: nurseId },
    { headers: authHeaders() }
  );
  return res?.data;
}

// ─── Dropdown data for the add form ───────────────────────────────────────
// The same endpoints FacilityUsers and DoctorsAddition already read; each one
// returns its list under its own key rather than a shared `data`.

export async function getFacilities() {
  const res = await axios.get(`${BASE}get_all_facilities/`, {
    headers: authHeaders(),
  });
  return Array.isArray(res?.data?.facility_list) ? res.data.facility_list : [];
}

export async function getStates() {
  const res = await axios.get(`${BASE}get_all_states/`, {
    headers: authHeaders(),
  });
  return Array.isArray(res?.data?.state_list) ? res.data.state_list : [];
}

export async function getDistricts(stateName) {
  const res = await axios.post(
    `${BASE}get_filtered_districts/`,
    { state_name: stateName },
    { headers: authHeaders() }
  );
  return Array.isArray(res?.data?.district_list) ? res.data.district_list : [];
}
